/**
 * Flights Popups Selectors
 *
 * Memoized selectors for accessing centralized flights popup state.
 * Using createSelector ensures components only re-render when selected data changes.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { API_ERROR_POPUP_KEY } from '../slices/popupsSlice';

// ============================================
// Base Selectors
// ============================================

const selectFlightsPopupsState = (state: RootState) => state.flights.popups;

// ============================================
// Popup Selectors (Memoized)
// ============================================

/**
 * Selector for the current popup key
 */
export const selectFlightsCurrentPopup = createSelector(
  [selectFlightsPopupsState],
  (popups) => popups.currentPopup,
);

/**
 * Selector for the popup map
 */
export const selectFlightsPopupMap = createSelector(
  [selectFlightsPopupsState],
  (popups) => popups.popupMap,
);

/**
 * Selector for the current popup data based on currentPopup key.
 * Returns the popup data from popupMap if currentPopup is set, otherwise null.
 */
export const selectFlightsCurrentPopupData = createSelector(
  [selectFlightsPopupsState],
  (popups) => {
    if (!popups.currentPopup) {
      return null;
    }
    return popups.popupMap[popups.currentPopup] ?? null;
  },
);

/**
 * Selector to check if error popup should be shown.
 * Returns true if currentPopup is the error popup key.
 */
export const selectIsFlightsErrorPopupVisible = createSelector(
  [selectFlightsCurrentPopup],
  (currentPopup) => currentPopup === API_ERROR_POPUP_KEY,
);

/**
 * Selector for error popup data specifically.
 * Returns the error popup data if it exists, otherwise null.
 */
export const selectFlightsErrorPopupData = createSelector(
  [selectFlightsPopupMap],
  (popupMap) => popupMap[API_ERROR_POPUP_KEY] ?? null,
);
